import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Card                                                              */
/* ------------------------------------------------------------------ */

export interface CardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Card header title. */
  title?: React.ReactNode;
  /** Subtitle / description below the title. */
  description?: React.ReactNode;
  /** Footer area (typically actions). */
  footer?: React.ReactNode;
  /** Remove default padding from the body area. */
  noPadding?: boolean;
}

const css = /* css */ `
  .sf-card {
    background: var(--sf-bg-elevated);
    border: 1px solid var(--sf-border);
    border-radius: var(--sf-radius-lg);
    box-shadow: var(--sf-shadow-sm);
    overflow: hidden;
  }

  .sf-card__header {
    padding: var(--sf-space-5) var(--sf-space-5) var(--sf-space-0);
    display: flex;
    flex-direction: column;
    gap: var(--sf-space-1);
  }

  .sf-card__title {
    font-size: var(--sf-text-md);
    font-weight: var(--sf-weight-semibold);
    color: var(--sf-text);
    line-height: var(--sf-leading-tight);
  }

  .sf-card__description {
    font-size: var(--sf-text-sm);
    color: var(--sf-text-muted);
    line-height: var(--sf-leading-normal);
  }

  .sf-card__body {
    padding: var(--sf-space-5);
  }

  .sf-card__body--flush {
    padding: 0;
  }

  .sf-card__footer {
    padding: var(--sf-space-4) var(--sf-space-5);
    border-top: 1px solid var(--sf-border);
    background: var(--sf-bg-inset);
    display: flex;
    align-items: center;
    gap: var(--sf-space-3);
    justify-content: flex-end;
  }
`;

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    { title, description, footer, noPadding = false, children, className, ...rest },
    ref,
  ) => {
    injectStyles("sf-card", css);

    const classes = ["sf-card", className ?? ""].filter(Boolean).join(" ");

    const hasHeader = title || description;

    return (
      <div ref={ref} className={classes} {...rest}>
        {hasHeader && (
          <div className="sf-card__header">
            {title && <div className="sf-card__title">{title}</div>}
            {description && (
              <div className="sf-card__description">{description}</div>
            )}
          </div>
        )}
        <div className={`sf-card__body${noPadding ? " sf-card__body--flush" : ""}`}>
          {children}
        </div>
        {footer && <div className="sf-card__footer">{footer}</div>}
      </div>
    );
  },
);

Card.displayName = "Card";
export { Card };
